/**
 * Hotlist Tab Component
 * Patient hotlist view for RN Command Center
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search, Mic, CalendarPlus, ChevronRight, CheckCircle, AlertCircle,
  Clock, Shield, Calendar, UserCheck, TrendingDown, Heart, Activity, AlertTriangle, ThermometerSun
} from "lucide-react";
import { PRIORITY_CONFIG, CATEGORY_CONFIG } from "./constants";

const HotlistTab = ({
  isDark,
  hotlist,
  searchQuery,
  setSearchQuery,
  priorityFilter,
  setPriorityFilter,
  categoryFilter,
  setCategoryFilter,
  setSelectedPatient,
  setActiveTab,
  startVoiceSession,
  openQuickAction
}) => {
  const navigate = useNavigate();
  
  // Icon mapping for categories
  const CATEGORY_ICONS = {
    overdue_tasks: Clock,
    compliance_risk: Shield,
    recert_due: Calendar,
    f2f_needed: UserCheck,
    decline_risk: TrendingDown,
    new_admission: Heart,
    symptom_alert: Activity,
    visit_overdue: Calendar,
    pain_escalation: AlertTriangle
  };

  // Filter hotlist
  const filteredHotlist = hotlist.filter(p => {
    if (priorityFilter !== "all" && p.priority !== priorityFilter) return false;
    if (categoryFilter !== "all" && p.category !== categoryFilter) return false;
    if (searchQuery && !p.patient_name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  return (
    <>
      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${isDark ? "text-slate-400" : "text-gray-400"}`} />
          <Input
            placeholder="Search patients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`pl-9 ${isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-200"}`}
            data-testid="hotlist-search"
          />
        </div>
        
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className={`w-36 ${isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-200"}`} data-testid="priority-filter">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className={`w-44 ${isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-200"}`} data-testid="category-filter">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Hotlist Cards */}
      <ScrollArea className="h-[600px]">
        <div className="space-y-2">
          {filteredHotlist.length === 0 ? (
            <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200"}>
              <CardContent className="p-8 text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <p className={isDark ? "text-slate-400" : "text-gray-500"}>No patients match current filters</p>
              </CardContent>
            </Card>
          ) : (
            filteredHotlist.map((patient) => {
              const priorityConfig = PRIORITY_CONFIG[patient.priority] || PRIORITY_CONFIG.medium;
              const categoryConfig = CATEGORY_CONFIG[patient.category] || {};
              const CategoryIcon = CATEGORY_ICONS[patient.category] || AlertCircle;
              
              return (
                <Card
                  key={`${patient.patient_id}-${patient.category}`}
                  className={`border-l-4 ${
                    patient.priority === 'critical' ? 'border-l-red-500' :
                    patient.priority === 'high' ? 'border-l-amber-500' :
                    'border-l-slate-400'
                  } ${isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-sm"}`}
                  data-testid={`hotlist-patient-${patient.patient_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${priorityConfig.color}`}>
                          <CategoryIcon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold">{patient.patient_name}</h3>
                            <Badge className={`${priorityConfig.color} text-white text-xs`}>
                              {priorityConfig.label}
                            </Badge>
                            <Badge variant="outline" className={`text-xs ${categoryConfig.color}`}>
                              {categoryConfig.label}
                            </Badge>
                          </div>
                          <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                            {patient.reason}
                          </p>
                          {patient.days_overdue > 0 && (
                            <p className="text-xs text-red-500 mt-1">{patient.days_overdue} days overdue</p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setSelectedPatient(patient); setActiveTab("voice"); startVoiceSession(patient); }}
                          data-testid={`voice-btn-${patient.patient_id}`}
                        >
                          <Mic className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openQuickAction(patient, 'schedule_visit')}>
                          <CalendarPlus className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-orange-500 border-orange-500 hover:bg-orange-50"
                          onClick={() => { setSelectedPatient(patient); setActiveTab("clinical"); }}
                        >
                          <ThermometerSun className="w-4 h-4" />
                        </Button>
                        <Button size="sm" onClick={() => navigate(`/hos?patient=${patient.patient_id}`)}>
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </ScrollArea>
    </>
  );
};

export default HotlistTab;
